import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { FilterIcon, XIcon } from 'lucide-react';
interface FilterOption {
  id: string;
  label: {
    en: string;
    ar: string;
  };
}
interface ExerciseFiltersProps {
  selectedFilters: {
    difficulty: string[];
    type: string[];
    topic: string[];
  };
  onFilterChange: (category: 'difficulty' | 'type' | 'topic', value: string) => void;
  onClearFilters: () => void;
}
const DIFFICULTIES: FilterOption[] = [{
  id: 'beginner',
  label: {
    en: 'Beginner',
    ar: 'مبتدئ'
  }
}, {
  id: 'intermediate',
  label: {
    en: 'Intermediate',
    ar: 'متوسط'
  }
}, {
  id: 'advanced',
  label: {
    en: 'Advanced',
    ar: 'متقدم'
  }
}];
const EXERCISE_TYPES: FilterOption[] = [{
  id: 'multiple-choice',
  label: {
    en: 'Multiple Choice',
    ar: 'اختيار من متعدد'
  }
}, {
  id: 'fill-blank',
  label: {
    en: 'Fill in the Blanks',
    ar: 'املأ الفراغات'
  }
}, {
  id: 'matching',
  label: {
    en: 'Matching',
    ar: 'مطابقة'
  }
}];
const TOPICS: FilterOption[] = [{
  id: 'grammar',
  label: {
    en: 'Grammar',
    ar: 'قواعد'
  }
}, {
  id: 'vocabulary',
  label: {
    en: 'Vocabulary',
    ar: 'مفردات'
  }
}, {
  id: 'conversation',
  label: {
    en: 'Conversation',
    ar: 'محادثة'
  }
}];
export function ExerciseFilters({
  selectedFilters,
  onFilterChange,
  onClearFilters
}: ExerciseFiltersProps) {
  const {
    isArabic
  } = useLanguage();
  const hasActiveFilters = Object.values(selectedFilters).some(filters => filters.length > 0);
  return <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-361">
      <div className="flex items-center justify-between mb-6" data-id="element-362">
        <div className="flex items-center gap-2" data-id="element-363">
          <FilterIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" data-id="element-364" />
          <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-365">
            {isArabic ? 'تصفية التمارين' : 'Filter Exercises'}
          </h3>
        </div>
        {hasActiveFilters && <button onClick={onClearFilters} className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1" data-id="element-366">
            <XIcon className="w-4 h-4" data-id="element-367" />
            <span data-id="element-368">{isArabic ? 'مسح التصفية' : 'Clear Filters'}</span>
          </button>}
      </div>
      <div className="space-y-6" data-id="element-369">
        {/* Difficulty */}
        <div data-id="element-370">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-371">
            {isArabic ? 'المستوى' : 'Difficulty'}
          </h4>
          <div className="flex flex-wrap gap-2" data-id="element-372">
            {DIFFICULTIES.map(difficulty => <button key={difficulty.id} onClick={() => onFilterChange('difficulty', difficulty.id)} className={`
                  px-3 py-1 rounded-full text-sm font-medium transition-colors
                  ${selectedFilters.difficulty.includes(difficulty.id) ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}
                `} data-id="element-373">
                {isArabic ? difficulty.label.ar : difficulty.label.en}
              </button>)}
          </div>
        </div>
        {/* Exercise Type */}
        <div data-id="element-374">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-375">
            {isArabic ? 'نوع التمرين' : 'Exercise Type'}
          </h4>
          <div className="flex flex-wrap gap-2" data-id="element-376">
            {EXERCISE_TYPES.map(type => <button key={type.id} onClick={() => onFilterChange('type', type.id)} className={`
                  px-3 py-1 rounded-full text-sm font-medium transition-colors
                  ${selectedFilters.type.includes(type.id) ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}
                `} data-id="element-377">
                {isArabic ? type.label.ar : type.label.en}
              </button>)}
          </div>
        </div>
        {/* Topics */}
        <div data-id="element-378">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3" data-id="element-379">
            {isArabic ? 'المواضيع' : 'Topics'}
          </h4>
          <div className="flex flex-wrap gap-2" data-id="element-380">
            {TOPICS.map(topic => <button key={topic.id} onClick={() => onFilterChange('topic', topic.id)} className={`
                  px-3 py-1 rounded-full text-sm font-medium transition-colors
                  ${selectedFilters.topic.includes(topic.id) ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}
                `} data-id="element-381">
                {isArabic ? topic.label.ar : topic.label.en}
              </button>)}
          </div>
        </div>
      </div>
    </div>;
}